import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Target, Award, Shield, Globe } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Compassion',
      description: 'We approach every community with empathy and care, understanding the unique challenges faced by both Indian and African societies.'
    },
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We maintain the highest standards of transparency and ethical practices in all our cross-continental operations.'
    },
    {
      icon: Users,
      title: 'Unity',
      description: 'We believe in the power of international collaboration and building strong partnerships between India and Africa.'
    },
    {
      icon: Target,
      title: 'Excellence',
      description: 'We strive for excellence in every aspect of our work, from healthcare delivery to educational programs.'
    }
  ];

  const milestones = [
    { year: '2018', title: 'Foundation Established', description: 'AIM Foundation was founded with a vision to bridge India and Africa through health and education.' },
    { year: '2019', title: 'First India-Africa Partnership', description: 'Successfully established our first cross-continental healthcare partnership program.' },
    { year: '2020', title: 'Digital Platform Launch', description: 'Launched our online platform connecting healthcare professionals and educators across continents.' },
    { year: '2021', title: '1000+ Beneficiaries', description: 'Reached a milestone of 1000+ lives impacted through our programs.' },
    { year: '2022', title: 'Multi-Country Expansion', description: 'Extended our services to 10+ countries across India and Africa.' },
    { year: '2023', title: 'Educational Programs Launch', description: 'Launched comprehensive educational initiatives benefiting communities in both regions.' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-800 to-orange-600 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            About AIM Foundation
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Dedicated to bridging India and Africa through health, hope, and education initiatives
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                To bridge the gap between India and Africa by fostering cross-continental partnerships in healthcare, education, and community development, creating sustainable impact across both regions.
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                We believe in the power of international collaboration and cultural exchange to address global health challenges and educational disparities.
              </p>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                Our Vision
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                To become the leading international organization fostering India-Africa partnerships, setting standards for cross-continental collaboration in health, education, and community development.
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                We envision a future where India and Africa work together as equal partners, sharing knowledge, resources, and innovations to create thriving communities on both continents.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 mb-4">
              <value.icon className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Numbers */}
      <section className="py-16 bg-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Our Impact
            </h2>
            <p className="text-xl opacity-90">
              Numbers that tell our story of bridging continents
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">10,000+</div>
              <div className="text-lg opacity-90">Lives Impacted</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">5,000+</div>
              <div className="text-lg opacity-90">Program Participants</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">500+</div>
              <div className="text-lg opacity-90">Partnerships Formed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">20+</div>
              <div className="text-lg opacity-90">Countries Reached</div>
            </div>
          </div>
        </div>
      </section>

      {/* Journey */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Our Journey
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Milestones that mark our path to making a difference
            </p>
          </div>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-px h-full w-0.5 bg-gray-200 dark:bg-gray-700"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}>
                  {/* Timeline dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-green-600 rounded-full border-4 border-white dark:border-gray-800"></div>
                  
                  {/* Content */}
                  <div className={`w-5/12 ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                    <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                      <div className="text-green-600 font-bold text-lg mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Join Us in Bridging Continents
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Every partnership matters, every collaboration counts. Be part of our mission to create stronger bonds between India and Africa through health, hope, and education.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-colors duration-200"
            >
              Become a Donor
            </Link>
            <button className="border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white px-8 py-3 rounded-lg font-semibold text-lg transition-colors duration-200">
              Support Our Cause
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
